/**
 * RoutingCandidatePanel — the evidence panel behind one routing-desk candidate
 * (DIGEMP-MANAGER-ROUTING-DESK-FRONTEND-046).
 *
 * Readiness/load comes from the existing read-only workspace projection
 * (GET /api/agents/:id/workspace) and handoff history from the existing conversation records
 * (GET /api/conversations/?agent_id=). A failed or empty source is shown as failed or empty —
 * never as "free" or "no history" when it is actually unknown.
 *
 * The operator's explicit choice uses the existing permission-gated conversation flow
 * (POST /api/conversations/ with an agent target) — the desk assigns nothing itself, and the
 * request text is only carried over as a draft the operator still reviews and sends.
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { agentsApi, conversationsApi } from "../services/api";
import EmployeeAvatar, { STATUS_META } from "./EmployeeAvatar";
import { conversationStatusLabel } from "./conversationStatusVocabulary";

function fmt(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("he-IL");
  } catch {
    return "";
  }
}

function HandoffRow({ conversation, candidateId }) {
  const parts = (conversation.participants || [])
    .filter((p) => p.role === "agent" && String(p.agent_id) === String(candidateId))
    .sort((a, b) => String(b.joined_at || "").localeCompare(String(a.joined_at || "")))
    .slice(0, 2);

  return (
    <li className="rounded-lg border border-gray-100 bg-gray-50/60 p-2.5 space-y-1">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
          {conversationStatusLabel(conversation.status)}
        </span>
        <span className="text-gray-400 mr-auto">{fmt(conversation.updated_at)}</span>
      </div>
      {parts.map((p) => (
        <div key={p.id} className="text-xs text-gray-600 space-y-0.5">
          <div>
            הצטרף/ה: {fmt(p.joined_at)}
            {p.left_at ? ` · עזב/ה: ${fmt(p.left_at)}` : p.is_active ? " · פעיל/ה בשיחה" : ""}
          </div>
          <div className={p.handoff_reason ? "text-violet-700" : "text-gray-400"}>
            {p.handoff_reason ? `סיבת ההפניה: ${p.handoff_reason}` : "ללא העברה מתועדת"}
          </div>
        </div>
      ))}
      {parts.length === 0 && <div className="text-xs text-gray-400">לא נמצא רישום השתתפות לעובד זה בשיחה</div>}
      <Link to={`/conversations/${conversation.id}`} className="text-xs text-blue-600 hover:underline">
        לשיחה
      </Link>
    </li>
  );
}

export default function RoutingCandidatePanel({ candidate, requestText = "", customerId = "", canRun = false, onClose }) {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  const workspaceQuery = useQuery({
    queryKey: ["routing-desk-workspace", candidate.id],
    queryFn: () => agentsApi.workspace(candidate.id),
  });
  const historyQuery = useQuery({
    queryKey: ["routing-desk-handoffs", candidate.id],
    queryFn: () => conversationsApi.list({ agent_id: candidate.id, limit: 8 }),
  });

  const workspace = workspaceQuery.data;
  const history = historyQuery.data || [];

  const openConversation = async () => {
    setCreating(true);
    setCreateError(null);
    try {
      const conversation = await conversationsApi.create({
        ...(customerId ? { customer_id: customerId } : {}),
        target: { kind: "agent", agent_id: candidate.id },
      });
      navigate(`/conversations/${conversation.id}`, {
        state: requestText.trim() ? { routingRequest: requestText.trim() } : undefined,
      });
    } catch (err) {
      setCreateError(err?.response?.data?.detail || "שגיאה בפתיחת השיחה");
      setCreating(false);
    }
  };

  const statusMeta = workspace ? STATUS_META[workspace.agent?.effective_status] : null;

  return (
    <aside aria-label={`פרטי העובד ${candidate.name}`} className="bg-white rounded-xl border shadow-sm p-4 space-y-3">
      <div className="flex items-center gap-2">
        <EmployeeAvatar
          name={candidate.name}
          avatarKey={candidate.avatarKey}
          avatarColor={candidate.avatarColor}
          status={candidate.effectiveAvailability || candidate.availability}
          showStatus
        />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-gray-800">{candidate.name}</div>
          {candidate.roleTitle && <div className="text-xs text-gray-500">{candidate.roleTitle}</div>}
        </div>
        {onClose && (
          <button type="button" onClick={onClose} aria-label="סגור את פרטי העובד" className="text-gray-400 hover:text-gray-600">
            ✕
          </button>
        )}
      </div>

      {candidate.specialties.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {candidate.specialties.map((s) => (
            <span key={s} className="text-[11px] px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-200">
              {s}
            </span>
          ))}
        </div>
      )}

      <div className="border-t border-gray-100 pt-2 space-y-1.5">
        <h3 className="text-xs font-semibold text-gray-700">מוכנות ועומס</h3>
        {workspaceQuery.isLoading && <p className="text-xs text-gray-400">טוען מצב מוכנות...</p>}
        {workspaceQuery.isError && (
          <p role="alert" className="text-xs text-red-600">לא ניתן לטעון את מצב המוכנות והעומס של העובד</p>
        )}
        {workspace && (
          <div className="text-xs text-gray-600 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`px-2 py-0.5 rounded-full ${statusMeta ? "bg-gray-100 text-gray-700" : "bg-gray-100 text-gray-500"}`}>
                {statusMeta ? statusMeta.label : workspace.agent?.effective_status}
              </span>
              {workspace.capabilities && (
                <span className="text-gray-400">
                  {workspace.capabilities.connections?.length || 0} חיבורים · {workspace.capabilities.tools?.length || 0} כלים מורשים
                </span>
              )}
            </div>
            {workspace.current_work ? (
              <div className="text-amber-700">
                עובד/ת כרגע על {workspace.current_work.ticket_number ? `קריאה #${workspace.current_work.ticket_number}` : "משימה"}
                {" · "}{workspace.current_work.run_status_label}
              </div>
            ) : (
              <div className="text-green-700">פנוי/ה — אין משימה פעילה</div>
            )}
            <div>
              קריאות בטיפול: {workspace.assigned_tickets?.length || 0} · שיחות אחרונות: {workspace.recent_conversations?.length || 0}
            </div>
            {(workspace.assigned_tickets || []).slice(0, 3).map((t) => (
              <div key={t.run_id}>
                <Link to={`/tickets/${t.ticket_id}?tab=ai-diagnosis`} className="text-blue-600 hover:underline">
                  {t.ticket_number ? `קריאה #${t.ticket_number}` : "קריאה"} — {t.run_status_label}
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="border-t border-gray-100 pt-2 space-y-1.5">
        <h3 className="text-xs font-semibold text-gray-700">היסטוריית הפניות</h3>
        {historyQuery.isLoading && <p className="text-xs text-gray-400">טוען היסטוריית הפניות...</p>}
        {historyQuery.isError && (
          <p role="alert" className="text-xs text-red-600">לא ניתן לטעון את היסטוריית ההפניות</p>
        )}
        {!historyQuery.isLoading && !historyQuery.isError && history.length === 0 && (
          <p className="text-xs text-gray-400 italic">אין שיחות קודמות עם עובד זה</p>
        )}
        <ul className="space-y-2 max-h-64 overflow-y-auto">
          {history.map((c) => (
            <HandoffRow key={c.id} conversation={c} candidateId={candidate.id} />
          ))}
        </ul>
      </div>

      <div className="border-t border-gray-100 pt-2 space-y-1.5">
        {createError && (
          <p role="alert" className="text-xs text-red-600">{createError}</p>
        )}
        {canRun ? (
          <button
            type="button"
            onClick={openConversation}
            disabled={creating}
            className="w-full px-3 py-2 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            {creating ? "פותח שיחה..." : `פתח שיחה עם ${candidate.name}${requestText.trim() ? " והמשך עם הפנייה" : ""}`}
          </button>
        ) : (
          <p className="text-xs text-gray-500">
            נדרשת הרשאת הפעלת עובדים (agents_run) כדי לפתוח שיחה עם העובד מתוך הדסק.
          </p>
        )}
      </div>
    </aside>
  );
}
